import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RoubleIcon from '@mui/icons-material/CurrencyRuble';
import { useAllNomenclatures } from '../../shared/useAllNomenclatures';
import { NomenclatureListItem } from '../../types';
import { generateId } from '../../helpers';
import { EMPTY_ITEM } from '../constants';
import {
  getItemTotalPrice,
  formatPrice,
} from '../../WarehouseIncomesPage/helpers';
import { WarehouseIncomeItemValues } from '../../WarehouseIncomesPage/services';

type Props = {
  onAdd: (item: WarehouseIncomeItemValues) => void;
  disabled?: boolean;
};

type NumericField = 'quantity' | 'purchasePrice';

const INITIAL_STATE = {
  nomenclature: null as NomenclatureListItem | null,
  quantity: '1',
  purchasePrice: '',
  batchNumber: '',
  comment: '',
};

export const AddItemForm = ({ onAdd, disabled }: Props) => {
  const [form, setForm] = useState(INITIAL_STATE);

  const { nomenclatures } = useAllNomenclatures();

  const handleNumberChange =
    (field: NumericField) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(',', '.');
      if (val === '' || /^\d*\.?\d*$/.test(val)) {
        setForm((prev) => ({ ...prev, [field]: val }));
      }
    };

  const handleTextChange =
    (field: 'batchNumber') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const qtyNumber = Number(form.quantity);
  const priceNumber = Number(form.purchasePrice);

  const total = getItemTotalPrice({
    quantity: qtyNumber,
    purchasePrice: priceNumber,
  });

  const isButtonDisabled =
    !form.nomenclature ||
    form.purchasePrice === '' ||
    Number.isNaN(qtyNumber) ||
    qtyNumber <= 0 ||
    Number.isNaN(priceNumber) ||
    priceNumber < 0;

  const handleAdd = () => {
    if (!form.nomenclature || isButtonDisabled) return;

    const newItem: WarehouseIncomeItemValues = {
      ...EMPTY_ITEM,
      id: generateId(),
      nomenclatureId: form.nomenclature.id,
      nomenclatureName: form.nomenclature.name,
      nomenclatureUnitOfMeasurement: form.nomenclature.unitOfMeasurement,
      quantity: qtyNumber,
      purchasePrice: priceNumber,
      batchNumber: form.batchNumber,
      comment: form.comment,
    };

    onAdd(newItem);
    setForm(INITIAL_STATE);
  };

  const handleClearQuantity = () =>
    setForm((prev) => ({ ...prev, quantity: '' }));
  const handleClearPrice = () =>
    setForm((prev) => ({ ...prev, purchasePrice: '' }));
  const handleClearBatch = () =>
    setForm((prev) => ({ ...prev, batchNumber: '' }));

  return (
    <Stack
      gap={2}
      p={2}
      sx={{
        flexDirection: { md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
      }}
    >
      <Autocomplete
        options={nomenclatures ?? []}
        value={form.nomenclature}
        onChange={(_, newValue) =>
          setForm((prev) => ({ ...prev, nomenclature: newValue }))
        }
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Выберите номенклатуру"
            size="small"
          />
        )}
        noOptionsText="Не найдено"
        disabled={disabled}
        fullWidth
      />
      <Stack gap={2} flexDirection="row">
        <TextField
          label="Кол-во"
          type="text"
          inputMode="decimal"
          size="small"
          value={form.quantity}
          onChange={handleNumberChange('quantity')}
          disabled={disabled}
          sx={{ width: 100 }}
          InputProps={{
            endAdornment: form.quantity ? (
              <InputAdornment position="end">
                <Tooltip title="Очистить">
                  <IconButton size="small" onClick={handleClearQuantity}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <TextField
          label="Цена за ед."
          type="text"
          inputMode="decimal"
          size="small"
          value={form.purchasePrice}
          onChange={handleNumberChange('purchasePrice')}
          disabled={disabled}
          sx={{ width: 160 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.purchasePrice ? (
                  <Tooltip title="Очистить">
                    <IconButton size="small" onClick={handleClearPrice}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <RoubleIcon fontSize="small" />
                )}
              </InputAdornment>
            ),
          }}
        />
        <Stack
          gap={1}
          flexDirection="row"
          justifyContent="stretch"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1,
            backgroundColor: 'action.selected',
            minWidth: 110,
            textAlign: 'center',
            flexGrow: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography component="span" color="text.disabled">
            =
          </Typography>
          <Tooltip title="Итоговая стоимость">
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ flex: 1, textAlign: 'right' }}
            >
              {formatPrice(total)}
            </Typography>
          </Tooltip>
        </Stack>
        <TextField
          label="Партия"
          size="small"
          value={form.batchNumber}
          onChange={handleTextChange('batchNumber')}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 120 }}
          InputProps={{
            endAdornment: form.batchNumber ? (
              <InputAdornment position="end">
                <Tooltip title="Очистить">
                  <IconButton size="small" onClick={handleClearBatch}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Stack>
      <Button
        type="button"
        variant="contained"
        disableElevation
        onClick={handleAdd}
        disabled={disabled || isButtonDisabled}
        startIcon={<AddIcon />}
        sx={{ flexShrink: 0 }}
      >
        Добавить
      </Button>
    </Stack>
  );
};
